import React from 'react';
import moment from 'moment/moment';
import { func } from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  addErrorMessageAction, setTaskFulfilled, setTaskPending
} from '../../../actions';
import { getSurveySchemas } from '../../../api';
import { langType, publicationStatusValues, surveyTypeValues } from '../../../constants/types';
import FoldableList from '../../FoldableList';
import { withLang } from '../../../i18n';
import { API_DATE_FORMAT, DISPLAY_DATE_FORMAT, routes } from '../../../constants';
import { getCompiledPath, getLocalizedText } from '../../../util';

import styles from './surveySchemaList.module.css';

const schemasDefaultValue = Object.values(surveyTypeValues)
  .reduce((prev, curr) => ({ ...prev, [curr]: [] }), {});

const SurveySchemaList = ({
  dispatchSetTaskPending,
  dispatchSetTaskFulfilled,
  dispatchAddErrorMessage,
  translate: t,
  lang
}) => {
  const [schemas, setSchemas] = React.useState(schemasDefaultValue);
  const [showArchived, setShowArchived] = React.useState(false);

  React.useEffect(() => {
    const statuses = showArchived
      ? [publicationStatusValues.ARCHIVED]
      : [publicationStatusValues.PUBLISHED, publicationStatusValues.DRAFT];
    dispatchSetTaskPending();
    getSurveySchemas(0, 999, statuses)
      .then((value) => {
        setSchemas(value.content.reduce((prev, curr) => ({
          ...prev,
          [curr.type]: [...prev[curr.type], curr]
        }), schemasDefaultValue));
      })
      .catch(dispatchAddErrorMessage)
      .finally(dispatchSetTaskFulfilled);
  }, [showArchived]);

  const onShowArchivedChange = () => {
    setShowArchived(!showArchived);
  };

  const makeStatusText = (status, useCount) => (
    (useCount && status !== publicationStatusValues.ARCHIVED)
      ? `${t('status.IN_USE')} (${useCount})`
      : t(`status.${status}`)
  );

  const renderSurveySchemaPartRows = () => (schemaArray) => (
    <div className={styles.listContainer}>
      <div className={styles.listHeader}>{t('surveySchemas.surveySchemaList.header.name')}</div>
      <div className={styles.listHeader}>{t('surveySchemas.surveySchemaList.header.code')}</div>
      <div className={styles.listHeader}>{t('surveySchemas.surveySchemaList.header.version')}</div>
      <div className={styles.listHeader}>{t('surveySchemas.surveySchemaList.header.status')}</div>
      <div className={styles.listHeader}>{t('surveySchemas.surveySchemaList.header.lastEdited')}</div>
      {schemaArray.map((schema) => (
        <React.Fragment key={schema.id}>
          <div data-cy="survey-schema-list-name">
            <Link to={getCompiledPath(routes.surveySchemas.schemas.schema.index, { surveySchemaId: schema.id })}>
              {getLocalizedText(schema.title, lang, t('surveyParts.titleMissing'))}
            </Link>
          </div>
          <div data-cy="survey-schema-list-code">{schema.code}</div>
          <div data-cy="survey-schema-list-version">{schema.version}</div>
          <div data-cy="survey-schema-list-status">{makeStatusText(schema.status, schema.useCount)}</div>
          <div data-cy="survey-schema-list-last-edited">{moment(schema.lastModifiedDate, API_DATE_FORMAT).format(DISPLAY_DATE_FORMAT)}</div>
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <div>
      {Object.values(surveyTypeValues).map((surveyType) => (
        <div className={styles.schemaTypeContainer} key={surveyType} data-cy="survey-schema-list">
          <div className={styles.titleContainer}>
            <h3>{t(`surveySchemas.surveySchemaList.types.${surveyType}`)} ({schemas[surveyType].length})</h3>
            <div>
              <input type="checkbox" id="show-archived" checked={showArchived} onChange={onShowArchivedChange} />
              <label htmlFor="show-archived">{t('manageSurveys.showArchived')}</label>
            </div>
          </div>
          <FoldableList
            foldThreshold={10}
            noElementsMessage="Ei elementtejä"
            renderFunction={renderSurveySchemaPartRows()}
          >
            {schemas[surveyType]}
          </FoldableList>
        </div>
      ))}
    </div>
  );
};

SurveySchemaList.propTypes = {
  dispatchSetTaskPending: func.isRequired,
  dispatchSetTaskFulfilled: func.isRequired,
  dispatchAddErrorMessage: func.isRequired,
  ...langType
};

const mapDispatchToProps = (dispatch) => ({
  dispatchSetTaskPending: () => dispatch(setTaskPending()),
  dispatchSetTaskFulfilled: () => dispatch(setTaskFulfilled()),
  dispatchAddErrorMessage: (err) => dispatch(addErrorMessageAction(err))
});

export default withLang(connect(null, mapDispatchToProps)(SurveySchemaList));
